import { useEffect, useReducer } from 'react';

export interface HasSideEffect<A> {
  sideEffect?: (dispatch: React.Dispatch<A>) => void;
}

/**
 * This hook allows you to add asynchronous side effects to a reducer.
 *
 * At the moment, we mainly use it to allow the reducer to handle network requests,
 * instead of requiring the React component to do so.
 *
 * To perform a side effect when the reducer enters a specific state, add a `sideEffect` property to that state.
 * This property should be a function that accepts a dispatch function as an argument.
 *
 * When you return this state from your reducer, the component will receive the updated state, and then the
 * `sideEffect` function will be automatically be called.
 *
 * Inside the `sideEffect` function, perform the work that needs to be done. When the work is complete,
 * send the appropriate action to `sideEffect` function's `dispatch` argument. This will trigger your
 * reducer's normal state transition logic.
 *
 * @example
 *
 * ### Your React component
 *
 * ```tsx
 * const [componentState, componentDispatch] = useReducerWithSideEffects(myReducer, initialState);
 *
 * const handleClick = () => {
 *   componentDispatch({ type: 'REQUEST_DATA' });
 * };
 * ```
 *
 * ### Your reducer
 *
 * ```ts
 * const myReducer = (state, action) => {
 *   switch (action.type) {
 *     case 'REQUEST_DATA':
 *       // This will set the React component's `componentState` to `{name: 'DATA_LOADING'}`:
 *       return {
 *         name: 'DATA_LOADING',
 *         // This `sideEffect` function will be called automatically after the component's `componentState`
 *         // is updated.
 *         // This `dispatch` argument is basically the same `componentDispatch` function
 *         // that your React component is using.
 *         sideEffect: async (dispatch) => {
 *           try {
 *             const data = await getData();
 *             // This will call `myReducer` with the `DATA_LOADED` action.
 *             dispatch({ type: 'DATA_LOADED', data });
 *           } catch (err) {
 *             // This will call `myReducer` with the `DATA_LOAD_ERROR` action.
 *             dispatch({ type: 'DATA_LOAD_ERROR', error: err });
 *           }
 *         },
 *       };
 *     // This is where you'll modify the state based on the action that you dispatched
 *     // when your side effect's network request was successful.
 *     case 'DATA_LOADED':
 *      return {
 *        name: 'SUCCESS',
 *        data: action.data,
 *      }
 *     // This is where you'll modify the state based on the action that you dispatched
 *     // when your side effect's network request failed.
 *     case 'DATA_LOAD_ERROR':
 *      return {
 *        name: 'ERROR',
 *        error: action.error,
 *      }
 *     default:
 *       return state;
 *   }
 * };
 * ```
 */
export const useReducerWithSideEffects = <T extends HasSideEffect<A>, A>(
  reducer: (state: T, action: A) => T,
  initialState: T,
  loggingEnabled: boolean = false,
): [T, React.Dispatch<A>] => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (loggingEnabled) {
      console.log(`Reducer state changed to ${JSON.stringify(state)}`);
    }
    if (state.sideEffect) {
      if (loggingEnabled) {
        console.log(`Calling side effect on state ${JSON.stringify(state)}`);
      }
      state.sideEffect(dispatch);
    }
  }, [state]);

  return [state, dispatch];
};
